import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, Spinner } from '../../../shared/components';
import { formatDate } from '../../../shared/utils/localization';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import {
  getWorkspaceBilling,
  selectBillingPlan,
  startBillingCheckout,
  startBillingTrial,
} from '../services/billingService';
import type {
  BillingCheckoutOutcome,
  BillingPaymentProviderCode,
  BillingPlan,
  WorkspaceBilling,
} from '../types/billing';
import styles from './PlansPage.module.css';

const CHECKOUT_OUTCOMES: BillingCheckoutOutcome[] = ['success', 'cancel', 'failed', 'pending'];

const formatUsd = (amount: number) => `$${amount}`;

const formatAzn = (amount: number, currency: string) => `${amount.toFixed(2)} ${currency}`;

/** Prices are shown in whichever currency the chosen provider settles in. */
const formatAmount = (amount: number, currency: string) =>
  (currency === 'USD' ? formatUsd(amount) : formatAzn(amount, currency));

export const PlansPage: React.FC = () => {
  const { t } = useTranslation('billing');
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = hasPermission(PermissionCodes.adminAccess)
    || hasPermission(PermissionCodes.companiesManage);

  const [workspace, setWorkspace] = React.useState<WorkspaceBilling | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busyCode, setBusyCode] = React.useState<string | null>(null);
  const [providerCode, setProviderCode] = React.useState<BillingPaymentProviderCode | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setWorkspace(await getWorkspaceBilling());
    } catch (loadError) {
      showToast(loadError instanceof Error ? loadError.message : t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Default to the first provider the workspace is offered, and drop a stale choice if the set changes.
  const providers = React.useMemo(() => workspace?.paymentProviders ?? [], [workspace]);
  React.useEffect(() => {
    if (providers.length === 0) {
      return;
    }

    setProviderCode((current: BillingPaymentProviderCode | null) => (
      current && providers.some(provider => provider.code === current) ? current : providers[0].code
    ));
  }, [providers]);

  const activeProvider = providers.find(provider => provider.code === providerCode) ?? providers[0] ?? null;
  const activeCurrency = activeProvider?.currency ?? workspace?.currency ?? 'AZN';
  const priceFor = React.useCallback(
    (plan: BillingPlan) => (activeCurrency === 'USD' ? plan.monthlyPriceUsd : plan.monthlyPriceAzn),
    [activeCurrency],
  );

  // Payriff redirects the browser back here after the hosted payment page. The payment itself is
  // confirmed server-side, so this only decides which message to show.
  const checkoutParam = searchParams.get('checkout');
  React.useEffect(() => {
    if (!checkoutParam) {
      return;
    }

    const outcome = CHECKOUT_OUTCOMES.includes(checkoutParam as BillingCheckoutOutcome)
      ? (checkoutParam as BillingCheckoutOutcome)
      : 'pending';

    showToast(
      t(`checkoutResult.${outcome}`),
      outcome === 'success' ? 'success' : outcome === 'pending' ? 'info' : 'error',
    );

    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  }, [checkoutParam, searchParams, setSearchParams, showToast, t]);

  const onStartTrial = async () => {
    if (!canManage) {
      return;
    }

    setBusyCode('trial');
    try {
      setWorkspace(await startBillingTrial());
      showToast(t('trialStarted'), 'success');
    } catch (startError) {
      showToast(startError instanceof Error ? startError.message : t('trialFailed'), 'error');
    } finally {
      setBusyCode(null);
    }
  };

  const onMarkEnterprise = async (plan: BillingPlan) => {
    setBusyCode(plan.code);
    try {
      setWorkspace(await selectBillingPlan(plan.code));
      showToast(t('enterpriseSelected'), 'success');
    } catch (selectError) {
      showToast(selectError instanceof Error ? selectError.message : t('selectFailed'), 'error');
    } finally {
      setBusyCode(null);
    }
  };

  const onSubscribe = async (plan: BillingPlan) => {
    if (!canManage) {
      return;
    }

    if (plan.code === 'enterprise') {
      await onMarkEnterprise(plan);
      return;
    }

    const planPrice = priceFor(plan);
    const isSwitch = workspace?.status === 'active' && workspace.selectedPlanCode !== plan.code;
    if (isSwitch && !window.confirm(t('confirmSwitch', {
      name: t(`plans.${plan.code}.name`),
      price: planPrice != null ? formatAmount(planPrice, activeCurrency) : '',
    }))) {
      return;
    }

    setBusyCode(plan.code);
    try {
      const checkout = await startBillingCheckout(plan.code, activeProvider?.code);
      if (!checkout.paymentUrl) {
        throw new Error(t('checkoutFailed'));
      }

      window.location.assign(checkout.paymentUrl);
    } catch (checkoutError) {
      showToast(checkoutError instanceof Error ? checkoutError.message : t('checkoutFailed'), 'error');
      setBusyCode(null);
    }
  };

  const currentPlan = workspace?.plans.find((plan) => plan.code === workspace.planCode) ?? null;
  const salesMailto = workspace
    ? `mailto:${workspace.contactEmail}?subject=${encodeURIComponent('Simaizeer Enterprise plan')}`
    : 'mailto:info@simaizeer.org';
  const statusValues = {
    plan: currentPlan ? t(`plans.${currentPlan.code}.name`) : '',
    days: workspace?.trialDaysRemaining ?? 0,
    date: workspace?.currentPeriodEndUtc ? formatDate(workspace.currentPeriodEndUtc) : '',
  };

  return (
    <div className={`company-admin-page ${styles.page}`}>
      <Breadcrumb items={[{ label: t('title'), path: '/plans' }]} />

      {loading && !workspace ? (
        <div className={styles.loadingState}>
          <Spinner size={32} />
        </div>
      ) : workspace ? (
        <>
          <section className={styles.hero}>
            <div className={styles.heroCopy}>
              <p className={styles.kicker}>{t('kicker')}</p>
              <h1>{t(`status.${workspace.status}.title`, statusValues)}</h1>
              <p className={styles.heroHelp}>{t(`status.${workspace.status}.help`, statusValues)}</p>
              {workspace.currentPeriodEndUtc && workspace.status === 'active' ? (
                <p className={styles.heroMeta}>{t('paidUntil', { date: statusValues.date })}</p>
              ) : null}
            </div>
            <div className={styles.heroActions}>
              {workspace.trialAvailable && canManage ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busyCode !== null}
                  onClick={() => void onStartTrial()}
                >
                  {busyCode === 'trial' ? t('startingTrial') : t('startTrial')}
                </button>
              ) : null}
              <a className="btn btn-secondary" href={salesMailto}>
                {t('talkToSales')}
              </a>
              <p className={styles.contactLine}>
                {t('contactLine')}
                {' '}
                <a href={salesMailto}>{workspace.contactEmail}</a>
                {' · '}
                <a href={`tel:${workspace.contactPhone}`}>{workspace.contactPhone}</a>
              </p>
            </div>
          </section>

          {!canManage ? <p className={styles.viewOnly}>{t('viewOnlyHelp')}</p> : null}
          {canManage && !workspace.checkoutEnabled ? (
            <p className={styles.viewOnly}>{t('checkoutDisabled')}</p>
          ) : null}

          {providers.length > 1 ? (
            <section className={styles.paymentChoice}>
              <div className={styles.paymentChoiceCopy}>
                <h2>{t('paymentChoiceTitle')}</h2>
                <p className={styles.muted}>{t('paymentChoiceHelp')}</p>
              </div>
              <div className={styles.paymentOptions} role="radiogroup" aria-label={t('paymentChoiceTitle')}>
                {providers.map((provider) => {
                  const selected = provider.code === activeProvider?.code;
                  return (
                    <button
                      key={provider.code}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`${styles.paymentOption} ${selected ? styles.paymentOptionSelected : ''}`}
                      disabled={busyCode !== null}
                      onClick={() => setProviderCode(provider.code)}
                    >
                      <span className={styles.paymentOptionName}>
                        {t(`providers.${provider.code}.name`)}
                      </span>
                      <span className={styles.paymentOptionNote}>
                        {t(`providers.${provider.code}.note`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          <div className={styles.planGrid}>
            {workspace.plans.map((plan) => (
              <PlanCard
                key={plan.code}
                plan={plan}
                price={priceFor(plan)}
                currency={activeCurrency}
                isCurrent={plan.code === workspace.planCode}
                isPaid={plan.code === workspace.selectedPlanCode && workspace.status === 'active'}
                trialCurrent={workspace.status === 'trial' && plan.code === workspace.planCode}
                needsRenewal={workspace.status === 'pastDue' && plan.code === workspace.selectedPlanCode}
                canManage={canManage}
                checkoutEnabled={workspace.checkoutEnabled}
                busy={busyCode === plan.code}
                disabled={busyCode !== null}
                salesMailto={salesMailto}
                onSubscribe={() => void onSubscribe(plan)}
              />
            ))}
          </div>

          <section className={styles.addons}>
            <h2>{t('addonsTitle')}</h2>
            <p className={styles.muted}>{t('addonsHelp')}</p>
            <ul>
              {workspace.addons.map((addon) => (
                <li key={addon.code}>
                  <strong>{t(`addons.${addon.code}.name`)}</strong>
                  <span>
                    {addon.monthlyPriceUsd != null
                      ? `${t('addonPrice', { price: formatUsd(addon.monthlyPriceUsd) })}${
                        addon.priceNoteKey ? ` ${t(`addons.${addon.code}.note`)}` : ''
                      }`
                      : t('addonCustom')}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <p className={styles.disclaimer}>{t('disclaimer', { currency: workspace.currency })}</p>
        </>
      ) : null}
    </div>
  );
};

type PlanCardProps = {
  plan: BillingPlan;
  /** Already resolved to the selected provider's currency; null for custom-priced plans. */
  price: number | null;
  currency: string;
  isCurrent: boolean;
  isPaid: boolean;
  trialCurrent: boolean;
  needsRenewal: boolean;
  canManage: boolean;
  checkoutEnabled: boolean;
  busy: boolean;
  disabled: boolean;
  salesMailto: string;
  onSubscribe: () => void;
};

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  price,
  currency,
  isCurrent,
  isPaid,
  trialCurrent,
  needsRenewal,
  canManage,
  checkoutEnabled,
  busy,
  disabled,
  salesMailto,
  onSubscribe,
}) => {
  const { t } = useTranslation('billing');
  const isEnterprise = plan.code === 'enterprise';
  const priceLabel = price != null
    ? t('pricePerMonth', { price: formatAmount(price, currency) })
    : t('customPrice');
  // The USD list price is only worth repeating when the card is priced in another currency.
  const usdLabel = currency !== 'USD' && plan.monthlyPriceUsd != null
    ? t('listPriceUsd', { price: formatUsd(plan.monthlyPriceUsd) })
    : null;

  const actionLabel = () => {
    if (busy) {
      return t('redirecting');
    }
    if (isEnterprise) {
      return t('markEnterprise');
    }
    if (needsRenewal) {
      return t('renewPlan');
    }
    if (trialCurrent) {
      return t('subscribeToPlan', { price: formatAmount(price ?? 0, currency) });
    }
    return t('payAndSubscribe', { price: formatAmount(price ?? 0, currency) });
  };

  const showAction = canManage && (isEnterprise ? !isPaid : (!isPaid || needsRenewal));

  return (
    <article
      className={`${styles.planCard} ${plan.highlighted ? styles.planCardHighlighted : ''} ${isCurrent ? styles.planCardCurrent : ''}`}
    >
      {plan.highlighted ? <span className={styles.popular}>{t('mostPopular')}</span> : null}
      {isCurrent ? (
        <span className={styles.currentBadge}>
          {trialCurrent ? t('trialPlan') : needsRenewal ? t('pastDuePlan') : t('currentPlan')}
        </span>
      ) : null}
      <h2>{t(`plans.${plan.code}.name`)}</h2>
      <p className={styles.planPitch}>{t(`plans.${plan.code}.pitch`)}</p>
      <p className={styles.price}>{priceLabel}</p>
      {usdLabel ? <p className={styles.priceNote}>{usdLabel}</p> : null}
      <p className={styles.limits}>
        {plan.includedUsers != null
          ? t('includedUsers', { count: plan.includedUsers })
          : t('customUsers')}
        {plan.includedActiveContactsPerMonth != null
          ? ` · ${t('includedContacts', { count: plan.includedActiveContactsPerMonth })}`
          : ''}
      </p>
      <ul className={styles.features}>
        {plan.featureKeys.map((key) => (
          <li key={key}>{t(`features.${key}`)}</li>
        ))}
      </ul>
      <div className={styles.planActions}>
        {isEnterprise ? (
          <a className="btn btn-secondary" href={salesMailto}>{t('contactSales')}</a>
        ) : null}
        {showAction ? (
          <button
            type="button"
            className={`btn ${plan.highlighted || needsRenewal ? 'btn-primary' : 'btn-secondary'}`}
            disabled={disabled || (!isEnterprise && !checkoutEnabled)}
            onClick={onSubscribe}
          >
            {actionLabel()}
          </button>
        ) : null}
        {isPaid && !needsRenewal ? (
          <span className={styles.currentNote}>{t('youAreHere')}</span>
        ) : null}
      </div>
    </article>
  );
};
